/**
 * Utility functions for DenaliActionPipeline dynamic forms
 */

import { query } from 'json-p3/dist/json-p3.esm'
import dayjs from 'dayjs'
import utc from 'dayjs/plugin/utc'

dayjs.extend(utc)

/**
 * Build react-hook-form validation rules from field definition
 * Uses extended format with value and message properties
 */
export const buildValidationRules = (field) => {
  const rules = {}

  // Handle required validation
  if (field.validations?.required || field.required) {
    rules.required = {
      value: true,
      message: 'This field is required.'
    }
  }

  // Handle validation rules
  if (field.validationRules) {
    const { min, max, pattern, minLength, maxLength } = field.validationRules

    if (min !== undefined) {
      rules.min = {
        value: min,
        message: `Minimum value is ${min}.`
      }
    }

    if (max !== undefined) {
      rules.max = {
        value: max,
        message: `Maximum value is ${max}.`
      }
    }

    if (minLength !== undefined) {
      rules.minLength = {
        value: minLength,
        message: `Minimum length is ${minLength} characters.`
      }
    }

    if (maxLength !== undefined) {
      rules.maxLength = {
        value: maxLength,
        message: `Maximum length is ${maxLength} characters.`
      }
    }

    if (pattern) {
      rules.pattern = {
        value: new RegExp(pattern),
        message: 'Invalid format.'
      }
    }
  }

  return rules
}

/**
 * Get default value for a field based on its editor type
 */
export const getDefaultValue = (field) => {
  if (field.defaultValue !== undefined) {
    return field.defaultValue
  }

  switch (field.editorType) {
    case 'hidden':
    case 'appcontext':
      return field.value ?? ''
    case 'bool':
      return false
    case 'number':
      return null
    case 'json':
      return null
    case 'select':
      return field.multiple ? [] : null
    case 'text':
    case 'textarea':
    default:
      return ''
  }
}

/**
 * Resolve JSON path expression against data
 * Returns resolved value or null if not found
 */
export const resolveJSONPath = (data, path) => {
  if (!data || !path) {
    return null
  }

  try {
    // json-p3 query syntax: query(path, data)
    const queryResult = query(path, data)
    // Results are objects with { value, location, root }
    return queryResult.nodes[0]?.value ?? null
  } catch (error) {
    console.warn('Failed to resolve JSON path:', path, error)
    return null
  }
}

/**
 * Normalize a value to the format expected by a field's editor type.
 * datetime-local inputs require YYYY-MM-DDThh:mm format (local time).
 * date inputs require YYYY-MM-DD format (local date).
 * Values ending in 'Z' or containing a TZ offset are parsed as UTC.
 */
export const normalizeFieldValue = (value, editorType) => {
  if (typeof value !== 'string' || value.length === 0) return value

  if (editorType === 'datetime') {
    const parsed = value.endsWith('Z') || /[+-]\d{2}:\d{2}$/.test(value)
      ? dayjs.utc(value).local()
      : dayjs(value)
    if (parsed.isValid()) return parsed.format('YYYY-MM-DDTHH:mm')
  }

  if (editorType === 'date') {
    if (value.includes('T') || value.endsWith('Z')) {
      const parsed = value.endsWith('Z') || /[+-]\d{2}:\d{2}$/.test(value)
        ? dayjs.utc(value).local()
        : dayjs(value)
      if (parsed.isValid()) return parsed.format('YYYY-MM-DD')
    }
  }

  return value
}

/**
 * Normalize sub-field values within a single object.
 */
const normalizeItemFields = (item, fields) => {
  if (!item || typeof item !== 'object') return item
  const result = { ...item }
  fields.forEach(field => {
    if (field.id in result) {
      result[field.id] = normalizeFieldValue(result[field.id], field.editorType)
    }
  })
  return result
}

/**
 * Normalize all values in an object based on field definitions from groups.
 * Handles format mismatches (e.g. ISO 8601 → datetime-local) for pre-filled forms.
 * For repeatable groups, normalizes each item in the array.
 */
export const normalizeFormDefaults = (values, groups) => {
  if (!values || !groups || !Array.isArray(groups)) return values
  const normalized = { ...values }

  groups.forEach(group => {
    const fields = Array.isArray(group.fields) ? group.fields : Object.values(group.fields || {})

    if (group.repeatable) {
      if (Array.isArray(normalized[group.id])) {
        normalized[group.id] = normalized[group.id].map(
          item => normalizeItemFields(item, fields)
        )
      }
      return
    }

    fields.forEach(field => {
      if (field.id in normalized) {
        normalized[field.id] = normalizeFieldValue(normalized[field.id], field.editorType)
      }
    })
  })

  return normalized
}

/**
 * Build default values object for react-hook-form from field groups
 * Resolves initFrom paths if initializationData is provided
 */
export const buildDefaultValues = (groups, initializationData) => {
  const defaultValues = {}

  if (!groups || !Array.isArray(groups)) {
    return defaultValues
  }

  groups.forEach(group => {
    if (group.repeatable) {
      defaultValues[group.id] = []
      return
    }

    const fields = Array.isArray(group.fields) ? group.fields : Object.values(group.fields || {})
    if (fields.length) {
      fields.forEach(field => {
        if (field.initFrom && initializationData) {
          const resolvedValue = resolveJSONPath(initializationData, field.initFrom)
          if (resolvedValue !== null && resolvedValue !== undefined) {
            defaultValues[field.id] = resolvedValue
            return
          }
        }

        if (initializationData && field.id in initializationData && initializationData[field.id] !== undefined) {
          defaultValues[field.id] = initializationData[field.id]
          return
        }

        defaultValues[field.id] = getDefaultValue(field)
      })
    }
  })

  return defaultValues
}

/**
 * Sort items by ordinal property
 */
export const sortByOrdinal = (items) => {
  if (!items || !Array.isArray(items)) {
    return []
  }

  return [...items].sort((a, b) => {
    const ordinalA = a.ordinal ?? Number.MAX_SAFE_INTEGER
    const ordinalB = b.ordinal ?? Number.MAX_SAFE_INTEGER
    return ordinalA - ordinalB
  })
}

/**
 * Parse JSON string safely
 */
export const parseJSON = (value) => {
  if (!value || typeof value !== 'string') {
    return null
  }

  try {
    return JSON.parse(value)
  } catch {
    return null
  }
}

/**
 * Stringify JSON safely with formatting
 */
export const stringifyJSON = (value) => {
  if (value === null || value === undefined) {
    return ''
  }

  try {
    return JSON.stringify(value, null, 2)
  } catch {
    return String(value)
  }
}

/**
 * Coerce a single value based on editor type.
 */
const coerceValue = (value, editorType) => {
  switch (editorType) {
    case 'number':
      return (value !== null && value !== '') ? Number(value) : value
    case 'bool':
      return Boolean(value)
    case 'json':
      return (typeof value === 'string') ? parseJSON(value) : value
    default:
      return value
  }
}

/**
 * Coerce form values to appropriate types for submission.
 * Pass groups to also handle repeatable group arrays.
 */
export const coerceFormValues = (values, fields, groups) => {
  const coerced = { ...values }

  const normalizedFields = Array.isArray(fields) ? fields : Object.values(fields || {})
  normalizedFields.forEach(field => {
    if (field.id in coerced) {
      coerced[field.id] = coerceValue(coerced[field.id], field.editorType)
    }
  })

  if (groups && Array.isArray(groups)) {
    groups.forEach(group => {
      if (!group.repeatable || !Array.isArray(coerced[group.id])) return
      const subFields = Array.isArray(group.fields)
        ? group.fields
        : Object.values(group.fields || {})
      coerced[group.id] = coerced[group.id].map(item => {
        const coercedItem = { ...item }
        subFields.forEach(sf => {
          if (sf.id in coercedItem) {
            coercedItem[sf.id] = coerceValue(coercedItem[sf.id], sf.editorType)
          }
        })
        return coercedItem
      })
    })
  }

  return coerced
}

/**
 * Get all top-level fields from non-repeatable groups.
 * Repeatable group fields are nested under the array and handled separately.
 */
export const getAllFields = (groups) => {
  if (!groups || !Array.isArray(groups)) {
    return []
  }

  return groups.reduce((acc, group) => {
    if (group.repeatable) return acc
    const fields = Array.isArray(group.fields) ? group.fields : Object.values(group.fields || {})
    return [...acc, ...fields]
  }, [])
}

/**
 * Extract a user-facing error message from an action result.
 * Returns null if the result indicates success.
 */
export const extractActionError = (result) => {
  if (!result || typeof result !== 'object' || result.success !== false) {
    return null
  }
  return result.message ||
    (result.error ? String(result.error) : null) ||
    (Array.isArray(result.errors) ? result.errors.join('; ') : null) ||
    'Action failed'
}

/**
 * Build args object by resolving argMapping paths against form data
 * @param {Object} formData - The submitted form data
 * @param {Object} argMapping - Mapping of arg names to JSON paths
 * @returns {Object} Resolved args object
 */
export const buildArgsFromMapping = (formData, argMapping) => {
  if (!argMapping || typeof argMapping !== 'object') {
    return {}
  }

  const args = {}

  Object.entries(argMapping).forEach(([argName, path]) => {
    const value = resolveJSONPath(formData, path)
    if (value !== null && value !== undefined) {
      args[argName] = value
    }
  })

  return args
}
